<template>
  <modal titulo="Gerar CNM em Lote" :loading="loading">

    <card titulo="Fichas selecionadas">
      <card-content>
        <div v-if="indicadores.length">
          <p class="font-weight-500 mb-1">Matrículas ({{ indicadores.length }})</p>
          <div class="small text-muted mb-2">As fichas elegíveis terão o CNM gerado; as inelegíveis serão puladas com motivo registrado.</div>
        </div>
        <div v-if="registros.length">
          <p class="font-weight-500 mb-1">Registros Auxiliares ({{ registros.length }})</p>
          <div class="small text-muted mb-2">As fichas elegíveis terão o CNM gerado; as inelegíveis serão puladas com motivo registrado.</div>
        </div>
        <p class="font-weight-bold">Total: {{ indicadores.length + registros.length }} ficha(s)</p>
      </card-content>
    </card>

    <card v-if="loteId" titulo="Resultado">
      <card-content>
        <div v-if="carregandoResultado" class="text-center py-3">
          <i class="fas fa-spinner fa-spin mr-2"></i> Processando...
        </div>
        <template v-else-if="lote">
          <div class="row mb-2">
            <div class="col-3 text-center">
              <div class="h4 text-success">{{ lote.total_sucesso }}</div>
              <div class="small">Sucesso</div>
            </div>
            <div class="col-3 text-center">
              <div class="h4 text-warning">{{ lote.total_pulado }}</div>
              <div class="small">Pulado</div>
            </div>
            <div class="col-3 text-center">
              <div class="h4 text-danger">{{ lote.total_erro }}</div>
              <div class="small">Erro</div>
            </div>
            <div class="col-3 text-center">
              <div class="h4">{{ lote.total_itens }}</div>
              <div class="small">Total</div>
            </div>
          </div>

          <div v-if="!lote.concluido" class="alert alert-info mb-2">
            <i class="fas fa-clock mr-1"></i>
            Processando em segundo plano ({{ processados }}/{{ lote.total_itens }})...
          </div>
          <div v-else class="alert alert-success mb-2">
            <i class="fas fa-check mr-1"></i> Processamento concluído.
          </div>

          <div v-if="lote.itens && lote.itens.length" style="max-height: 300px; overflow-y: auto;">
            <table class="table table-sm table-bordered mb-0">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Código</th>
                  <th>Status</th>
                  <th>CNM / Motivo</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in lote.itens" :key="item.id">
                  <td>{{ item.tipo === 'INDICADOR_REAL' ? 'Matrícula' : 'Reg. Auxiliar' }}</td>
                  <td>{{ item.codigo }}{{ item.livro_letra ? '-' + item.livro_letra : '' }}</td>
                  <td>
                    <span :class="classeStatus(item.status)">{{ item.status }}</span>
                  </td>
                  <td class="small">{{ item.cnm || item.mensagem || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </card-content>
    </card>

    <template #footer>
      <button type="button" @click.prevent="fechar()" class="btn btn-outline-secondary mr-auto">
        Fechar
      </button>
      <v-button v-if="!loteId" :run="confirmar" class="btn btn-primary">
        Confirmar Geração
      </v-button>
      <v-button v-if="loteId && lote && !lote.concluido" :run="atualizar" class="btn btn-outline-info">
        Atualizar
      </v-button>
      <v-button v-if="loteId && lote" :run="verRelatorio" class="btn btn-outline-secondary">
        Ver Relatório
      </v-button>
    </template>

  </modal>
</template>

<script>
import CnmLoteBusiness from "@/business/cnm/CnmLoteBusiness.js";
import FrontBusiness from "@/business/FrontBusiness.js";
import Card from "@/components/Layout/components/Card.vue";
import CardContent from "@/components/Layout/components/CardContent.vue";
import Modal from "@/components/Modal.vue";
import ConsultarCnmLote from "@/components/IndicadorReal/ConsultarCnmLote.vue";

export default {
  name: "GerarCnmLote",
  components: { Modal, Card, CardContent },
  props: {
    indicadores: { type: Array, default: () => [] },
    registros: { type: Array, default: () => [] },
  },
  data() {
    return {
      loading: false,
      loteId: null,
      lote: null,
      carregandoResultado: false,
      poolInterval: null,
    };
  },
  computed: {
    processados() {
      if (!this.lote) return 0;
      return (this.lote.total_sucesso || 0) + (this.lote.total_pulado || 0) + (this.lote.total_erro || 0);
    },
  },
  beforeDestroy() {
    this.pararPool();
  },
  methods: {
    fechar() {
      this.pararPool();
      FrontBusiness.closeModal(this.loteId);
    },

    async confirmar() {
      try {
        const ids = this.indicadores.map(f => f.id);
        const regIds = this.registros.map(f => f.id);
        this.loteId = await CnmLoteBusiness.iniciar(ids, regIds);
        FrontBusiness.showSuccess('', 'Lote iniciado! O processamento ocorre em segundo plano.');
        await this.atualizar();
        this.iniciarPool();
      } catch (e) {
        FrontBusiness.showError('Erro', e);
      }
    },

    async atualizar() {
      if (!this.loteId) return;
      this.carregandoResultado = true;
      try {
        this.lote = await CnmLoteBusiness.detalhar(this.loteId);
        if (this.lote?.concluido) {
          this.pararPool();
        }
      } catch (e) {
        FrontBusiness.showError('Erro ao atualizar', e);
      } finally {
        this.carregandoResultado = false;
      }
    },

    iniciarPool() {
      this.pararPool();
      this.poolInterval = setInterval(async () => {
        if (this.lote?.concluido) {
          this.pararPool();
          return;
        }
        await this.atualizar();
      }, 5000);
    },

    pararPool() {
      if (this.poolInterval) {
        clearInterval(this.poolInterval);
        this.poolInterval = null;
      }
    },

    classeStatus: CnmLoteBusiness.classeStatus,

    verRelatorio() {
      FrontBusiness.openModal(ConsultarCnmLote, { loteId: this.loteId });
    },
  },
};
</script>
